import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const personalities = [
  {
    name: "The Guide",
    key: "guide",
    description: "Calm, peaceful, mindfulness",
    color: "#14B8A6",
    gradient: "from-teal-500 to-teal-600",
    image: "https://static.prod-images.emergentagent.com/jobs/311886e7-2d11-4ea1-a20b-3280d5e92476/images/826c9ffd4257976971a8e3bad649c904f7a03dd263f425f6ab798b5a75141e16.png"
  },
  {
    name: "The Career Coach",
    key: "friend",
    description: "Career planning & Decision making skills",
    color: "#FACC15",
    gradient: "from-yellow-400 to-yellow-500",
    image: "https://static.prod-images.emergentagent.com/jobs/311886e7-2d11-4ea1-a20b-3280d5e92476/images/b0a782ead939fb625fd4e33032e723aa8528c7adec3343dbed027220453dad01.png"
  },
  {
    name: "The Mentor",
    key: "mentor",
    description: "Communication & Growth",
    color: "#6366F1",
    gradient: "from-indigo-500 to-indigo-600",
    image: "https://static.prod-images.emergentagent.com/jobs/311886e7-2d11-4ea1-a20b-3280d5e92476/images/7ae80a0192a48d4fe31b8c625ed78bad7b4290254e83888ec3eb5e618261208a.png"
  },
  {
    name: "The Coach",
    key: "coach",
    description: "Confidence & Goal setting",
    color: "#EF4444",
    gradient: "from-red-500 to-red-600",
    image: "https://images.pexels.com/photos/4428040/pexels-photo-4428040.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
  }
];

export default function Dashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [dailyTip, setDailyTip] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDailyTip();
  }, []);

  const fetchDailyTip = async () => {
    try {
      const response = await axios.get(`${API}/tips/daily`);
      setDailyTip(response.data);
    } catch (error) {
      console.error("Error fetching daily tip:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePersonalitySelect = (key) => {
    navigate(`/chat/${key}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50 to-pink-50" data-testid="dashboard-page">
      <nav className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-extrabold text-slate-800">
              Your<span className="gradient-text">AImentor</span>
            </h1>
            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-600">Hi, {user.name}!</span>
              {user.role === "admin" && (
                <Button onClick={() => navigate("/admin")} variant="ghost" className="rounded-full text-orange-600 font-bold" data-testid="nav-admin-btn">
                  Admin
                </Button>
              )}
              <Button onClick={() => navigate("/history")} variant="ghost" className="rounded-full" data-testid="nav-history-btn">
                History
              </Button>
              <Button onClick={() => navigate("/assessments")} variant="ghost" className="rounded-full" data-testid="nav-assessments-btn">
                Assessments
              </Button>
              <Button onClick={onLogout} variant="outline" className="rounded-full" data-testid="nav-logout-btn">
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-6 md:px-8 py-12">
        {!user.email_verified && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-yellow-50 border-2 border-yellow-200 rounded-2xl flex items-center justify-between gap-4"
            data-testid="verification-banner"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <p className="font-bold text-yellow-800">Verify your email to unlock unlimited chats</p>
                <p className="text-sm text-yellow-700">You have {Math.max(0, 3 - (user.messages_sent || 0))} free messages remaining. Check your inbox for the verification link.</p>
              </div>
            </div>
            <Button
              onClick={async () => {
                try {
                  const token = localStorage.getItem('token');
                  await axios.post(`${API}/auth/resend-verification`, {}, { headers: { Authorization: `Bearer ${token}` } });
                  toast.success("Verification email sent!");
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Failed to send");
                }
              }}
              className="btn-primary text-sm whitespace-nowrap"
              data-testid="resend-verification-btn"
            >
              Resend Email
            </Button>
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-extrabold mb-4">
            Welcome Back, <span className="gradient-text">{user.name}</span>
          </h2>
          <p className="text-lg text-slate-600">Choose a companion to continue your growth journey</p>
        </motion.div>

        {!loading && dailyTip && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-12"
          >
            <Card className="bg-gradient-to-br from-orange-500 to-pink-500 text-white p-8 border-0 shadow-xl" data-testid="daily-tip-card">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold mb-2">Daily Inspiration</h3>
                  <p className="text-white/90 text-lg leading-relaxed mb-2">{dailyTip.tip}</p>
                  <span className="inline-block px-3 py-1 bg-white/20 rounded-full text-sm font-semibold">
                    {dailyTip.category}
                  </span>
                </div>
              </div>
            </Card>
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h3 className="text-2xl font-bold mb-6">Select Your Companion</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {personalities.map((persona, index) => (
              <motion.div
                key={persona.key}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + index * 0.1 }}
                whileHover={{ y: -8 }}
              >
                <Card
                  className={`overflow-hidden border-2 persona-${persona.key} card-hover cursor-pointer h-full`}
                  onClick={() => handlePersonalitySelect(persona.key)}
                  data-testid={`select-personality-${persona.key}`}
                >
                  <div className="aspect-square overflow-hidden relative">
                    <img
                      src={persona.image}
                      alt={persona.name}
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                    />
                    <div className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t ${persona.gradient} p-4`}>
                      <h4 className="text-xl font-bold text-white">{persona.name}</h4>
                    </div>
                  </div>
                  <div className="p-6">
                    <p className="text-slate-600">{persona.description}</p>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6"
        >
          <Card 
            className="p-8 card-hover cursor-pointer border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-white"
            onClick={() => navigate("/assessments")}
            data-testid="go-to-assessments-card"
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold">Self-Assessments</h3>
            </div>
            <p className="text-slate-600">Reflect on your progress and gain insights into your growth journey</p>
          </Card>

          <Card 
            className="p-8 card-hover cursor-pointer border-2 border-teal-200 bg-gradient-to-br from-teal-50 to-white"
            onClick={() => navigate("/history")}
            data-testid="go-to-history-card"
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold">Conversation History</h3>
            </div>
            <p className="text-slate-600">Review your past conversations and track your personal development</p>
          </Card>
        </motion.div>
      </main>
    </div>
  );
}